/**
 * The four verbs, against `/v1`.
 *
 * THE PREFIX LIVES HERE AND NOWHERE ELSE. Before this, one app's client took paths
 * relative to `/v1` and the other took the whole path, so shared code had to know
 * which it was talking to — and when it guessed wrong it built `/v1/v1/...`, which
 * surfaced as a hovercard spinning forever rather than as a build error. A route
 * module now writes the path it would read in the API docs MINUS the version, these
 * four add the version, and there is exactly one line in the repo that can get it
 * wrong.
 *
 * Named `apiVerb` rather than a bare `get`/`patch`: a route module has locals called
 * `patch` and `post`, and a helper that shadows one is a silent miscompile.
 *
 * `community()` in `./envelope` composes with them the same way: it returns
 * `/communities/{id}/…`, and the prefix is added here.
 *
 * No auth, no base URL, no error decoding — that is the app's transport
 * (`../transport`), which both surfaces install at startup.
 */

import { api } from '../transport';
import type { QueryParams } from './envelope';

/** The one place `/v1` is spelled. */
const v1 = (path: string): string => `/v1${path}`;

/**
 * Drop nulls and undefineds.
 *
 * Every v1 filter means "no filter" when absent, and the transports below serialise
 * whatever they are handed — a `null` would go up as the literal string "null" and be
 * read as a value.
 */
function clean(params: QueryParams): Record<string, string | number | string[]> {
  const out: Record<string, string | number | string[]> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined) continue;
    out[k] = typeof v === 'boolean' ? String(v) : (v as string | number | string[]);
  }
  return out;
}

export function apiGet<T>(path: string, params: QueryParams = {}): Promise<T> {
  return api().get<T>(v1(path), clean(params));
}

export function apiPost<T>(path: string, body: unknown = {}, params: QueryParams = {}): Promise<T> {
  return api().send<T>('POST', v1(path), body, clean(params));
}

export function apiPatch<T>(path: string, body: unknown = {}, params: QueryParams = {}): Promise<T> {
  return api().send<T>('PATCH', v1(path), body, clean(params));
}

export function apiPut<T>(path: string, body: unknown = {}, params: QueryParams = {}): Promise<T> {
  return api().send<T>('PUT', v1(path), body, clean(params));
}

/** `delete` is a reserved word. */
export function apiDelete<T>(path: string, params: QueryParams = {}): Promise<T> {
  return api().send<T>('DELETE', v1(path), undefined, clean(params));
}
